// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// path.ts — A* over 192 tiles, entirely in integers.
//
// Every number here is an integer because both devices run this and both have to get the same
// answer to the bit. That rules out a floating-point heuristic, and it rules out any tie-break that
// is not total: two tiles with the same f-score must be popped in the same order on both peers, so
// the heap key packs f into the high bits and the TILE INDEX into the low bits. Ties therefore break
// on tile index, which is the same everywhere, rather than on whatever order the heap happened to
// receive them in.
//
// Costs are in hundredths of a tile-step: open 100, scrub 167, a wreck 200. A diagonal step costs
// 181/128 of a cardinal one (1.414), and the octile heuristic uses the same ratio, so it never
// overestimates and A* stays optimal.

import { COLS, ROWS, TILES, Terrain, colOf, idx, rowOf, type MapData } from './map';

const DIAG_NUM = 181;
const DIAG_DEN = 128;

/** Cardinal first, then diagonals — a deliberate, fixed order so expansion is reproducible. */
const DIRS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
  [1, -1],
  [1, 1],
  [-1, 1],
  [-1, -1],
];

/** Scratch, reused across calls — a fresh Int32Array per path would dominate the sim's cost. */
const gScore = new Int32Array(TILES);
const cameFrom = new Int32Array(TILES);
const closed = new Uint8Array(TILES);
const stamp = new Int32Array(TILES);
let epoch = 0;

/** A binary heap of packed keys. `key = f * 256 + tile`, so f dominates and tile breaks every tie. */
const heap = new Int32Array(TILES * 8);
let heapN = 0;

function heapPush(key: number): void {
  let i = heapN++;
  heap[i] = key;
  while (i > 0) {
    const p = (i - 1) >> 1;
    if (heap[p] <= heap[i]) break;
    const t = heap[p];
    heap[p] = heap[i];
    heap[i] = t;
    i = p;
  }
}

function heapPop(): number {
  const top = heap[0];
  heap[0] = heap[--heapN];
  let i = 0;
  for (;;) {
    const l = i * 2 + 1;
    const r = l + 1;
    let m = i;
    if (l < heapN && heap[l] < heap[m]) m = l;
    if (r < heapN && heap[r] < heap[m]) m = r;
    if (m === i) break;
    const t = heap[m];
    heap[m] = heap[i];
    heap[i] = t;
    i = m;
  }
  return top;
}

/** Octile distance in the same hundredths, admissible because 100 is the cheapest tile. */
function octile(a: number, b: number): number {
  const dc = Math.abs(colOf(a) - colOf(b));
  const dr = Math.abs(rowOf(a) - rowOf(b));
  const lo = dc < dr ? dc : dr;
  const hi = dc < dr ? dr : dc;
  return 100 * (hi - lo) + 141 * lo;
}

export interface PathQuery {
  map: MapData;
  seat: number;
  /** Extra per-tile cost — a wreck slows you, and the bot adds threat here to route around danger. */
  extra: (tile: number) => number;
  /** Tiles nothing may enter, over and above the terrain (a Forge that is not yours). */
  blocked: (tile: number) => boolean;
}

/**
 * The tile list from `from` to `to`, excluding `from` and including `to`. Empty when there is no
 * route — the caller then walks as close as it can and tries again later, rather than freezing,
 * because a unit that stops for ever because a wreck landed in a doorway reads as a broken game.
 */
export function findPath(q: PathQuery, from: number, to: number): number[] {
  if (from === to) return [];
  epoch++;
  heapN = 0;
  const { map, seat } = q;

  const enterable = (t: number): boolean => {
    if (map.terrain[t] === Terrain.Ridge) return false;
    if (map.terrain[t] === Terrain.Forge && map.forgeOf[t] !== seat) return false;
    return !q.blocked(t);
  };

  if (!enterable(to)) {
    // Aim at the cheapest enterable neighbour instead of refusing the order outright.
    let best = -1;
    let bestH = 1 << 30;
    for (const [dc, dr] of DIRS) {
      const c = colOf(to) + dc;
      const r = rowOf(to) + dr;
      if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
      const n = idx(c, r);
      if (!enterable(n)) continue;
      const h = octile(from, n);
      const nk = seat === 0 ? n : TILES - 1 - n;
      const bk = best < 0 ? 1 << 30 : seat === 0 ? best : TILES - 1 - best;
      if (h < bestH || (h === bestH && nk < bk)) {
        bestH = h;
        best = n;
      }
    }
    if (best < 0) return [];
    to = best;
    if (from === to) return [];
  }

  // THE TIE-BREAK ROTATES WITH THE MAP. Packing the raw tile index means every equal-cost tie in
  // the search breaks toward tile 0, which is one particular corner of the board — so on a
  // 180°-symmetric map the two seats walk around the same obstacle in opposite senses and one of
  // them is systematically on the better side of it. Mirroring the key for seat 1 makes "which way
  // do I go round" a property of the geometry rather than of which end you started at.
  const key = (t: number): number => (seat === 0 ? t : TILES - 1 - t);
  const unkey = (k: number): number => (seat === 0 ? k : TILES - 1 - k);

  stamp[from] = epoch;
  gScore[from] = 0;
  cameFrom[from] = -1;
  closed[from] = 0;
  heapPush(octile(from, to) * 256 + key(from));

  let guard = 0;
  while (heapN > 0) {
    if (++guard > 4000) break;
    const cur = unkey(heapPop() & 255);
    if (stamp[cur] !== epoch || closed[cur]) continue;
    closed[cur] = 1;
    if (cur === to) break;

    const cc = colOf(cur);
    const cr = rowOf(cur);
    for (let d = 0; d < 8; d++) {
      const c = cc + DIRS[d][0];
      const r = cr + DIRS[d][1];
      if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
      const n = idx(c, r);
      if (!enterable(n)) continue;
      // No cutting a corner between two blocked tiles — a unit that slides through the diagonal gap
      // of an L-shaped ridge is walking through a wall.
      if (d >= 4 && (!enterable(idx(cc + DIRS[d][0], cr)) || !enterable(idx(cc, cr + DIRS[d][1])))) continue;

      const base = map.cost[n] + q.extra(n);
      const stepCost = d >= 4 ? ((base * DIAG_NUM) / DIAG_DEN) | 0 : base;
      const g = gScore[cur] + stepCost;
      if (stamp[n] === epoch && (closed[n] || g >= gScore[n])) continue;
      stamp[n] = epoch;
      closed[n] = 0;
      gScore[n] = g;
      cameFrom[n] = cur;
      heapPush((g + octile(n, to)) * 256 + key(n));
    }
  }

  if (stamp[to] !== epoch || !closed[to]) return [];
  const out: number[] = [];
  for (let t = to; t !== from && t >= 0; t = cameFrom[t]) out.push(t);
  out.reverse();
  return out;
}
